import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import DeprecatedRippleEffect, { DeprecatedRippleEffectProps } from 'components/RippleEffect/DeprecatedRippleEffect';
import styled from './styles';

const RIPPLE_STYLE = {
  height: '100%'
};

export interface BreadcrumbProps extends React.ComponentPropsWithRef<'div'> {
  /** Children prop is where content resides. */
  children: React.ReactNode;
  /** onClick handler */
  onClick?: React.MouseEventHandler;
  /** If true, this Breadcrumb is disabled */
  disabled?: boolean;
  /** Type of breadcrumbs. 'a' represents html a tag. 'div' represents html div tag */
  as?: 'a' | 'div';
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To alter the ripple effect DOM element attributes. */
  rippleEffectProps?: Partial<Omit<DeprecatedRippleEffectProps, 'ref'>>;
  /** tabindex configuration. */
  tabIndex?: number;
}

/**
 * Single breadcrumb.
 * Any other props than `children` and `as` will be provided to the root element. */
const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>((props: BreadcrumbProps, ref) => getContext(({ RTL, theme }) => {
  const {
    disabled,
    onClick,
    tabIndex,
    theme: themeProp,
    rippleEffectProps,
    as,
    children,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme ? theme.BREADCRUMBS : {}, themeProp);

  const handleClick = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick && onClick(event);
  };

  const wrapperProps = {
    RTL,
    disabled,
    onClick: handleClick,
    ref,
    tabIndex: tabIndex || 0,
    ...otherProps
  };

  return as === 'a' ?
    <styled.BreadcrumbLinkWrapper
      $theme={resolvedTheme}
      {...wrapperProps}
    >
      <DeprecatedRippleEffect
        disabled={disabled}
        style={{ ...RIPPLE_STYLE }}
        rippleStyle={{
          backgroundColor: resolvedTheme.rippleColor
        }}
        {...rippleEffectProps}
      >
        {children}
      </DeprecatedRippleEffect>
    </styled.BreadcrumbLinkWrapper>
    :
    <styled.BreadcrumbNormalWrapper
      $theme={resolvedTheme}
      {...wrapperProps}
    >
      <DeprecatedRippleEffect
        disabled={disabled}
        style={{ ...RIPPLE_STYLE }}
        rippleStyle={{
          backgroundColor: resolvedTheme.rippleColor
        }}
        {...rippleEffectProps}
      >
        {children}
      </DeprecatedRippleEffect>
    </styled.BreadcrumbNormalWrapper>;
}));

Breadcrumb.displayName = 'Breadcrumb';

Breadcrumb.defaultProps = {
  onClick: undefined,
  disabled: false,
  as: 'a',
  theme: {},
  rippleEffectProps: {},
  tabIndex: 0
};

export default Breadcrumb;
